import type { ReactElement } from "react"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { ContactSectionFlatData } from "@gatsby-plugin-generic-page/fragments/contactSection"
import { Text } from "@ionos-web-design-system/react"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"
import InfoBoxCard from "./InfoBoxCard"

export default function ContactSection({
  title,
  subtitle,
  contactDetails,
  images,
  infoBoxes,
  infoBoxesTitle,
}: ContactSectionFlatData): ReactElement<ContactSectionFlatData> | null {
  if (isEmpty(contactDetails)) return null

  const { text, prefix, number } = contactDetails[0]
  const desktopInfoBoxes = (infoBoxes ?? []).filter(
    (box) => !box.flatData.isVisibleOnSmallOnly
  )

  return (
    <section
      className="bg-surface-subtle"
      aria-label="contact"
      data-test-id="help"
    >
      <div className="max-w-[82rem] mx-auto pt-9 pb-8 px-3">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch">
          <div className="flex-1 bg-surface-base rounded-sm p-5 md:p-8 flex flex-col gap-5">
            <div className="flex flex-col gap-gap-8">
              <Text variant="heading5xl" color="base" asChild>
                <h2>{title}</h2>
              </Text>

              <Text variant="heading2xl" color="base" asChild>
                <p>{subtitle}</p>
              </Text>
            </div>

            <div className="flex flex-col gap-2">
              <Text variant="body" color="base">
                {text}
              </Text>

              <Text variant="heading4xl" weight="black" color="base">
                {prefix}&nbsp;{number}
              </Text>
            </div>
          </div>

          <div className="flex-1 rounded overflow-hidden">
            <Media
              media={images}
              className="h-full w-full [&_img]:object-cover [&_img]:h-full"
            />
          </div>
        </div>

        {!isEmpty(infoBoxes) ? (
          <div className="pt-8 flex flex-col items-center gap-7">
            {!isEmpty(infoBoxesTitle) ? (
              <Text variant="heading5xl" color="base" asChild>
                <h3>{infoBoxesTitle}</h3>
              </Text>
            ) : null}

            <div
              data-testid="info-boxes-row"
              className={cn(
                "flex flex-col lg:flex-row gap-4 w-full",
                desktopInfoBoxes.length < 3 ? "md:w-auto" : undefined
              )}
            >
              {infoBoxes.map((box) => (
                <InfoBoxCard
                  key={box.id}
                  box={box}
                  className={
                    desktopInfoBoxes.length < 3 ? "md:min-w-[20rem]" : undefined
                  }
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
